/**
 * Application entry point.
 *
 * @remarks
 * This module exports a factory rather than mounting an app, because it runs twice: once per route
 * inside Node during the build, to produce static HTML, and once in the browser to hydrate it.
 * `ViteSSG` owns the router and the head instance so both passes share one of each — two routers
 * would let the rendered HTML and the hydrated app disagree about which page they are on.
 *
 * **Install order matters.** Pinia must be installed before any store is used. Anything that
 * touches `window` has to sit behind the `isClient` guard: during prerendering there is no browser,
 * and an unguarded access fails the build with a stack trace pointing at Vue internals rather than
 * at the offending line.
 *
 * @module
 * @author Bayu Dwiyan Satria
 * @since 1.0.0
 */

/**
 * Libraries
 */
import { createPinia } from 'pinia'
import { ViteSSG } from 'vite-ssg'

/**
 * Components
 */
import App from '@/App.vue'

/**
 * Plugins
 */
import { i18n } from '@/i18n'
import { routes } from '@/router/routes'
import { scrollBehavior } from '@bayudwiyansatria/vue/router'

/**
 * Composables
 */
import { reportWebVitals, useTheme } from '@bayudwiyansatria/vue'

/**
 * Config
 */
import { siteConfig } from '@/config/site'

/**
 * Styles
 */
import './style.css'

/** Height of the sticky header. Keep in step with `AppHeader.vue`. */
const HEADER_OFFSET = 72

export const createApp = ViteSSG(
  App,
  { routes, scrollBehavior: scrollBehavior({ headerOffset: HEADER_OFFSET }) },
  ({ app, isClient }) => {
    const pinia = createPinia()

    app.use(pinia)
    app.use(i18n)

    if (!isClient) {
      return
    }

    /*
     * Adopt the theme the inline boot script in index.html already applied — it reads the DOM rather
     * than resolving again, so the store cannot disagree with what is on screen. The script itself is
     * generated by `themeBootScript()`; see vite.config.ts.
     */
    useTheme().initialize()

    if (siteConfig.features.serviceWorker && import.meta.env.PROD && 'serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        void navigator.serviceWorker.register('/sw.js')
      })
    }

    if (siteConfig.features.webVitals) {
      /*
       * Replace this sink with a real collector. It is a no-op by default so the boilerplate ships
       * without an analytics vendor baked in.
       */
      reportWebVitals(metric => {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.debug(`[web-vitals] ${metric.name}`, metric.value, metric.rating)
        }
      })
    }
  }
)
